<template>
    <!-- Conteneur principal pleine largeur -->
    <div>
        <div>
            <div class="ppv2-rounded-lg ppv2-shadow-sm ppv2-p-4 md:ppv2-p-6">
                <!-- Message d'erreur -->
                <div v-if="error" class="ppv2-bg-red-50 ppv2-border ppv2-border-red-200 ppv2-rounded-lg ppv2-p-4 ppv2-mb-4">
                    <div class="ppv2-flex ppv2-items-center">
                        <svg class="ppv2-h-5 ppv2-w-5 ppv2-text-red-600 ppv2-mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span class="ppv2-text-red-800">{{ error }}</span>
                    </div>
                </div>

                <div class="d-flex justify-content-between align-items-center mb-4">
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-6">
                        <Header :title="$t('quotes.title')" />
                        <!-- Vue Toggle -->
                        <div
                            class="switch-view ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-1 ppv2-p-1 ppv2-rounded-lg ppv2-bg-black ppv2-bg-opacity-[7%] md:ppv2-mb-0 ppv2-mb-3"
                        >
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'ppv2-px-1 ppv2-rounded-lg ppv2-text-black ppv2-transition-all',
                                    viewMode === 'list' ? 'ppv2-bg-white dark:ppv2-bg-dark' : '',
                                ]"
                                :title="$t('quotes.view.list')"
                            >
                                <List :size="20" :stroke-width="1" />
                            </button>
                            <button
                                @click="viewMode = 'kanban'"
                                :class="[
                                    'ppv2-px-1 ppv2-rounded-lg ppv2-text-black ppv2-transition-all',
                                    viewMode === 'kanban' ? 'ppv2-bg-white dark:ppv2-bg-dark ppv2-shadow-sm' : '',
                                ]"
                                :title="$t('quotes.view.kanban')"
                            >
                                <SquareKanban :size="20" :stroke-width="1" />
                            </button>
                        </div>
                    </div>
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="15"
                        custom-class="ppv2-gap-2 ppv2-px-6"
                        @click="createQuote"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('quotes.create') }}</span>
                    </BaseButton>
                    <!-- Dropdown for create options if needed, simplified for now -->
                </div>

                <!-- Stats Cards -->
                <div
                    class="ppv2-max-w-full ppv2-mb-6 ppv2-grid ppv2-grid-cols-1 xl:ppv2-grid-cols-3 lg:ppv2-grid-cols-2"
                    :style="{ gap: normalizeSize(38, true) }"
                >
                    <!-- Tous les devis -->
                    <div class="cardCAFact ppv2-flex align-items-center ppv2-gap-1">
                        <div class="ppv2-px-4 ppv2-py-6 ppv2-text-white ppv2-w-[49%]">
                            <div :style="{ fontSize: normalizeFontSize(20) }">{{ $t('quotes.all_quotes') }}</div>
                            <div class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(25) }">
                                {{ formatCurrency(stats.all_quotes?.amount || 0) }} HT
                            </div>
                        </div>
                        <div class="ppv2-w-[1px] ppv2-h-[90%] ppv2-bg-white" />

                        <!-- Devis acceptés -->
                        <div class="ppv2-px-4 ppv2-py-6 ppv2-text-white ppv2-w-[49%]">
                            <div :style="{ fontSize: normalizeFontSize(20) }">{{ $t('quotes.accepted_quotes') }}</div>
                            <div class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(25) }">
                                {{ formatCurrency(stats.accepted_quotes?.amount || 0) }} HT
                            </div>
                        </div>
                    </div>

                    <!-- Devis en attente -->
                    <div class="ppv2-rounded-lg ppv2-bg-white ppv2-px-4 ppv2-py-6 ppv2-text-[#000000] ppv2-shadow-lg">
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                            <div class="ppv2-text-[#000000] ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(20) }">
                                {{ $t('quotes.pending_quotes') }}
                            </div>
                            <div
                                class="ppv2-bg-[#0075FF] ppv2-text-white ppv2-w-6 ppv2-h-6 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-full ppv2-text-xs poppinsMedium"
                            >
                                {{ stats.pending_quotes?.count || 0 }}
                            </div>
                        </div>
                        <div class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(25) }">
                            {{ formatCurrency(stats.pending_quotes?.amount || 0) }} HT
                        </div>
                    </div>
                </div>

                <!-- Filters Row -->
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row ppv2-justify-start md:ppv2-justify-between">
                    <div class="ppv2-mb-6 ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search
                                v-model="searchQuery"
                                :emitGlobal="false"
                                :keep-open-on-search="true"
                                @search-updated="handleSearchUpdate"
                            />
                        </div>

                        <!-- Date Range Picker -->
                        <invoice-date-filter
                            :start-date.sync="dateRange.start"
                            :end-date.sync="dateRange.end"
                            @change="handleDateChange"
                        >
                        </invoice-date-filter>

                        <!-- More Filters -->
                        <filter-dropdown
                            :label="$t('invoices.filter_status')"
                            :options="availableFilters.orderStatuses || []"
                            v-model="filters.status_id"
                            :multiple="true"
                            @change="handleFilterChange"
                        >
                            <template #icon>
                                <FileText :size="17" />
                            </template>
                        </filter-dropdown>

                        <filter-dropdown
                            :label="$t('invoices.filter_type')"
                            :options="availableFilters.types || []"
                            v-model="filters.type_id"
                            :multiple="true"
                            @change="handleFilterChange"
                        >
                            <template #icon>
                                <CalendarClock :size="18" />
                            </template>
                        </filter-dropdown>

                        <!-- Modifier les colonnes (mobile) — sans objet en kanban : il n'y a
                             pas de colonnes de tableau à configurer sur un board. -->
                        <div v-if="viewMode === 'list'" class="ppv2-relative md:ppv2-hidden" ref="columnsDropdownMobile">
                            <button
                                @click="showColumns = !showColumns"
                                class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-black ppv2-text-opacity-70 hover:ppv2-bg-black hover:ppv2-bg-opacity-[5%] ppv2-transition-colors"
                                :style="{ fontSize: normalizeFontSize(14) }"
                            >
                                <FilePen :size="18" />
                                <span>{{ $t('quotes.columns_short') }}</span>
                                <ChevronDown :size="16" />
                            </button>
                            <!-- Dropdown -->
                            <div
                                v-if="showColumns"
                                class="menuDropdown ppv2-absolute ppv2-top-full ppv2-left-0 ppv2-mt-2 ppv2-rounded-md ppv2-overflow-hidden ppv2-z-50"
                            >
                                <div class="ppv2-max-h-[300px] ppv2-overflow-y-auto">
                                    <label
                                        v-for="(column, index) in allColumns"
                                        :key="index"
                                        class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-1 ppv2-cursor-pointer ppv2-transition-colors"
                                    >
                                        <BaseCheckbox
                                            :value="selectedColumnKeys.includes(column.key)"
                                            @input="toggleColumn(column.key)"
                                            size="sm"
                                            :hasBorderChecked="true"
                                        />
                                        <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                            column.label
                                        }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Type Filter Pills + Modifier les colonnes.
                     Masqué en kanban : le board porte déjà le compte par statut en tête de
                     chaque colonne, et il n'a pas de colonnes de tableau à configurer. -->
                <div v-if="viewMode === 'list'" class="ppv2-mb-6 ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-3">
                    <button
                        v-for="filter in typeFilters"
                        :key="filter.id"
                        :class="[
                            'ppv2-rounded-full md:ppv2-px-4 ppv2-px-2 ppv2-py-1.5 ppv2-transition-colors poppinsMedium',
                            activeTypeFilter === filter.id ? 'filter-pill-active' : 'filter-pill-inactive',
                        ]"
                        :style="{ fontSize: normalizeFontSize(14) }"
                        @click="activeTypeFilter = filter.id"
                    >
                        {{ filter.label }}
                        <span
                            v-if="filter.count !== undefined"
                            :class="[
                                'ppv2-min-w-[24px] ppv2-h-[24px] ppv2-rounded-full ppv2-text-xs ppv2-px-1.5 ppv2-ml-1',
                                getBadgeColor(filter.id),
                            ]"
                        >
                            {{ filter.count }}
                        </span>
                    </button>
                    <!-- Vue Toggle moved to header -->

                    <div class="ppv2-flex-grow"></div>

                    <!-- Modifier les colonnes -->
                    <div class="ppv2-relative md:ppv2-block ppv2-hidden" ref="columnsDropdown">
                        <button
                            @click="showColumns = !showColumns"
                            class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-black ppv2-text-opacity-70 hover:ppv2-bg-black hover:ppv2-bg-opacity-[5%] ppv2-transition-colors"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <FilePen :size="18" />
                            <span>{{ $t('quotes.edit_columns') }}</span>
                            <ChevronDown :size="16" />
                        </button>
                        <!-- Dropdown -->
                        <div
                            v-if="showColumns"
                            class="menuDropdown ppv2-absolute ppv2-top-full ppv2-right-0 ppv2-mt-2 ppv2-rounded-md ppv2-overflow-hidden ppv2-z-50"
                        >
                            <div class="ppv2-max-h-[300px] ppv2-overflow-y-auto">
                                <label
                                    v-for="(column, index) in allColumns"
                                    :key="index"
                                    class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-1 ppv2-cursor-pointer ppv2-transition-colors"
                                >
                                    <BaseCheckbox
                                        :value="selectedColumnKeys.includes(column.key)"
                                        @input="toggleColumn(column.key)"
                                        size="sm"
                                        :hasBorderChecked="true"
                                    />
                                    <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                        column.label
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Devis DataTable -->
                <div v-if="viewMode === 'list'">
                    <devis-data-table
                        ref="devisDataTable"
                        :start-date="dateRange.start"
                        :end-date="dateRange.end"
                        :type-filter="activeTypeFilter"
                        :search-query="searchQuery"
                        :columns="visibleColumns"
                        :filters="filters"
                        @selection-changed="handleSelectionChanged"
                        @open-answer-detail="handleOpenAnswerDetail"
                    >
                    </devis-data-table>
                </div>

                <div v-else>
                    <devis-kanban
                        ref="devisKanban"
                        :start-date="dateRange.start"
                        :end-date="dateRange.end"
                        :type-filter="activeTypeFilter"
                        :search-query="searchQuery"
                        :filters="filters"
                        @open-answer-detail="handleOpenAnswerDetail"
                    >
                    </devis-kanban>
                </div>
            </div>
        </div>

        <!-- Advanced Filters Modal -->
        <invoice-advanced-filters
            v-if="showAdvancedFilters"
            :available-filters="availableFilters"
            :current-filters="filters"
            save-url="/back-office/devis-v3/save-filters"
            @close="showAdvancedFilters = false"
            @update="handleAdvancedFiltersUpdate"
        />

        <!-- Answer Detail Modal -->
        <devis-answer-detail-modal v-model="showAnswerDetail" :answer-id="selectedAnswerId" />
    </div>
</template>

<script>
import Search from '../V3/Experience/components/Search.vue'
import ExportSelect from '../Shared/ExportSelect.vue'
import DevisDataTable from './DevisDataTable.vue'
import DevisKanban from './DevisKanban.vue'
import FilterDropdown from '../Invoices/FilterDropdown.vue'
import InvoiceAdvancedFilters from '../Invoices/InvoiceAdvancedFilters.vue'
import DevisAnswerDetailModal from './DevisAnswerDetailModal.vue'
import normalizeFontSize from '../../utils/normalizeFontSize'
import Header from '../Shared/Header.vue'
import BaseCheckbox from '../Shared/BaseCheckbox.vue'
import BaseButton from '../Shared/BaseButton.vue'
import normalizeSize from '../../utils/normalizeSize'
import {
    FileText,
    Plus,
    ChevronDown,
    DownloadCloud,
    CalendarClock,
    FileEdit,
    LayoutGrid,
    FilePen,
    List,
    SquareKanban,
} from 'lucide-vue'

export default {
    name: 'DevisPage',
    components: {
        DevisDataTable,
        DevisKanban,
        Plus,
        ChevronDown,
        DownloadCloud,
        Search,
        ExportSelect,
        FileText,
        Header,
        CalendarClock,
        FileEdit,
        LayoutGrid,
        FilePen,
        BaseCheckbox,
        FilterDropdown,
        InvoiceAdvancedFilters,
        DevisAnswerDetailModal,
        List,
        SquareKanban,
        BaseButton,
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        allColumns() {
            return [
                { key: 'status', label: this.$t('quotes.columns.status'), sortable: true },
                { key: 'resource', label: this.$t('quotes.columns.type'), sortable: true },
                { key: 'created_at', label: this.$t('quotes.columns.created_at'), sortable: true },
                { key: 'emission', label: this.$t('quotes.columns.emission'), sortable: true },
                { key: 'due_date', label: this.$t('quotes.columns.due_date'), sortable: true },
                { key: 'customer', label: this.$t('quotes.columns.customer'), sortable: true },
                { key: 'fiscal_number', label: this.$t('quotes.columns.number'), sortable: true },
                { key: 'total', label: this.$t('quotes.columns.amount_ht'), sortable: true },
            ]
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    data() {
        return {
            loading: false,
            error: null,
            viewMode: 'list',
            activeTypeFilter: 'all',
            searchQuery: '',
            selectedCount: 0,
            showColumns: false,
            showAdvancedFilters: false,
            showAnswerDetail: false,
            selectedAnswerId: null,
            selectedColumnKeys: ['status', 'resource', 'created_at', 'emission', 'customer', 'fiscal_number', 'total'],
            dateRange: {
                start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
            },
            stats: {
                all_quotes: { amount: 0, count: 0 },
                accepted_quotes: { amount: 0, count: 0 },
                pending_quotes: { amount: 0, count: 0 },
            },
            typeFilters: [
                { id: 'all', label: this.$t('quotes.types.all'), count: 0 },
                { id: 'demande', label: this.$t('quotes.types.demande'), count: 0 },
                { id: 'envoye', label: this.$t('quotes.types.envoye'), count: 0 },
                { id: 'signe', label: this.$t('quotes.types.signe'), count: 0 },
                { id: 'paye', label: this.$t('quotes.types.paye'), count: 0 },
                { id: 'perdu', label: this.$t('quotes.types.perdu'), count: 0 },
            ],
            // New Filter Data
            filters: {
                status_id: [],
                type_id: [],
                has_refund: null,
                voucher_id: null,
                promo_code: null,
                payment_method_id: null,
                prestations: [],
                products: [],
                packages: [],
                series: [],
                staff_id: null,
                total_greater_than: null,
                total_lesser_than: null,
            },
            availableFilters: {
                orderStatuses: [],
                types: [], // To be populated
                staffs: [],
                paymentMethods: [],
                prestations: [],
                products: [],
                packages: [],
                series: [],
                vouchers: [],
                promotions: [],
            },
        }
    },
    mounted() {
        this.loadStats()
        this.loadTypeCounts()
        this.loadAvailableFilters()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    watch: {
        'dateRange.start'() {
            this.handleFilterChange()
        },
        'dateRange.end'() {
            this.handleFilterChange()
        },
        activeTypeFilter() {
            this.handleFilterChange()
        },
        // Les pastilles de type sont masquées en kanban : on remet le filtre à « Toutes »
        // en y entrant, sinon un filtre choisi en vue liste continuerait de restreindre le
        // board sans qu'aucun contrôle à l'écran ne le montre.
        viewMode(mode) {
            this.showColumns = false
            if (mode === 'kanban' && this.activeTypeFilter !== 'all') {
                this.activeTypeFilter = 'all'
            }
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        createQuote() {
            window.location.href = '/back-office/v3/devis/create'
        },
        handleDateChange() {
            this.handleFilterChange()
        },
        toggleColumn(key) {
            if (this.selectedColumnKeys.includes(key)) {
                this.selectedColumnKeys = this.selectedColumnKeys.filter((k) => k !== key)
            } else {
                this.selectedColumnKeys.push(key)
            }
        },
        handleClickOutside(event) {
            const refs = ['columnsDropdown', 'columnsDropdownMobile']
            const isInside = refs.some((name) => {
                const el = this.$refs[name]
                const nodes = Array.isArray(el) ? el : [el]
                return nodes.some((node) => node && node.contains(event.target))
            })
            if (!isInside) {
                this.showColumns = false
            }
        },
        getBadgeColor(filterId) {
            switch (filterId) {
                case 'demande':
                    return 'ppv2-bg-[#9C9C9C] ppv2-text-white'
                case 'envoye':
                    return 'ppv2-bg-[#0075FF] ppv2-text-white'
                case 'signe':
                    return 'ppv2-bg-[#2D9CDB] ppv2-text-white'
                case 'paye':
                    return 'ppv2-bg-[#035B2F] ppv2-text-white'
                case 'perdu':
                    return 'ppv2-bg-[#B3001B] ppv2-text-white'
                default:
                    return 'ppv2-bg-[#5932EA] ppv2-text-white'
            }
        },
        async loadStats() {
            try {
                this.loading = true
                const response = await this.axios.get('/back-office/devis-v3/stats', {
                    params: {
                        startDate: this.dateRange.start,
                        endDate: this.dateRange.end,
                    },
                })
                this.stats = response.data || response
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async loadTypeCounts() {
            try {
                const response = await this.axios.get('/back-office/devis-v3/type-counts', {
                    params: {
                        startDate: this.dateRange.start,
                        endDate: this.dateRange.end,
                    },
                })
                const data = response.data || response

                // Update counts but preserve structure
                if (data.filters) {
                    this.typeFilters = this.typeFilters.map((f) => {
                        const found = data.filters.find((apiF) => apiF.id === f.id)
                        return found ? { ...f, count: found.count } : f
                    })
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        async loadAvailableFilters() {
            try {
                const response = await this.axios.get('/back-office/devis-v3/filter-data')
                const data = response.data.data || response.data

                if (data) {
                    this.availableFilters = {
                        orderStatuses: data.orderStatuses || [],
                        types: (data.typesDevis || []).map((t) => ({
                            id: t.id,
                            label: t.name,
                        })),
                        staffs: data.staffs || [],
                        paymentMethods: data.paymentMethods || [],
                        prestations: data.prestations || [],
                        products: data.products || [],
                        packages: data.packages || [],
                        series: data.series || [],
                        vouchers: data.vouchers || [],
                        promotions: data.promotions || [],
                    }

                    // Add hardcoded types if not present
                    if (!this.availableFilters.types || this.availableFilters.types.length === 0) {
                        this.availableFilters.types = [
                            { id: 'Anniversaire', label: this.$t('quotes.types.anniversaire') },
                            { id: 'Entreprise', label: this.$t('quotes.types.entreprise') },
                            { id: 'EVG/EVJF', label: this.$t('quotes.types.evg_evjf') },
                            { id: 'Manuel', label: this.$t('quotes.types.manuel') },
                        ]
                    }
                    // Ensure statuses have label for consistent usage if they only come as name
                    if (this.availableFilters.orderStatuses) {
                        this.availableFilters.orderStatuses = this.availableFilters.orderStatuses.map((s) => ({
                            ...s,
                            label: s.translation_key ? this.$t(s.translation_key) : s.name, // Use i18n translation
                            id: s.id,
                        }))
                    }

                    // Load saved user filters
                    if (data.userFilters && data.userFilters.filters) {
                        // Merge saved filters with default structure to ensure reactivity
                        // Note: DevisPage doesn't have getDefaultFilters method shown in snippet,
                        // so we'll just merge with existing this.filters
                        this.filters = { ...this.filters, ...data.userFilters.filters }
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleAdvancedFiltersUpdate(newFilters) {
            this.filters = { ...this.filters, ...newFilters }
            this.handleFilterChange()
        },
        handleOpenAnswerDetail(answerId) {
            this.selectedAnswerId = answerId
            this.showAnswerDetail = true
        },
        handleFilterChange() {
            this.loadStats()
            this.loadTypeCounts()
            this.$nextTick(() => {
                if (this.viewMode === 'list' && this.$refs.devisDataTable) {
                    this.$refs.devisDataTable.reload()
                } else if (this.viewMode === 'kanban' && this.$refs.devisKanban) {
                    this.$refs.devisKanban.reload()
                }
            })
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.$nextTick(() => {
                if (this.viewMode === 'list' && this.$refs.devisDataTable) {
                    this.$refs.devisDataTable.handleSearchUpdate(searchValue)
                } else if (this.viewMode === 'kanban' && this.$refs.devisKanban) {
                    this.$refs.devisKanban.handleSearchUpdate(searchValue)
                }
            })
        },
        handleSelectionChanged(count) {
            this.selectedCount = count
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    @apply ppv2-bg-gray-900 ppv2-bg-opacity-80 ppv2-text-gray-400;
    margin-top: 10px;
    z-index: 9;
}

.dropdownItem:hover {
    @apply ppv2-bg-indigo-900 ppv2-text-white;
}

.dropdownItem:hover * {
    @apply ppv2-text-white;
}

.dark {
    .menuDropdown {
        @apply ppv2-bg-gray-800 ppv2-bg-opacity-90;
    }
}

.filter-btn {
    @apply ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-[#191D41] ppv2-rounded-xl ppv2-px-4 ppv2-py-2.5 ppv2-text-[#191D41] ppv2-bg-white hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-text-sm;
    font-family: 'Poppins Light', sans-serif;
}

.filter-pill-active {
    @apply ppv2-bg-black ppv2-text-white ppv2-shadow-lg;
}

.filter-pill-inactive {
    @apply ppv2-bg-white ppv2-text-[#021832] ppv2-border ppv2-border-gray-200 hover:ppv2-bg-gray-50;
}

.dark {
    .filter-pill-active {
        @apply ppv2-bg-white ppv2-text-black;
    }

    .filter-pill-inactive {
        @apply ppv2-bg-transparent ppv2-text-gray-300;
    }
}

.cardCAFact {
    background-image: url('/media/bo/backgounds/bgCardFacture.png');
    background-size: cover;
    background-position: center;
    box-shadow: -1px 1px 6.7px 0px #00000040;
    border-radius: 15px;
}

.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

/* Soften the Search component border to match the filter bar aesthetic */
:deep(.search-container.borderOpacity) {
    border-color: rgba(0, 0, 0, 0.2) !important;
}

.dark .switch-view {
    border: 1px solid white !important;
}
</style>
